import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isoWeek from "dayjs/plugin/isoWeek";
import updateLocale from "dayjs/plugin/updateLocale";

import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "dayjs/locale/vi";
import "dayjs/locale/en";

import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { ControlpanelPrimaryButton } from "./StyledButton";
import CalendarV2Icon16pxSvg from "../assets/images/icons/CalendarV2Icon16pxSvg";

dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(updateLocale);

// Thứ 2 đầu tuần, CN cuối tuần
dayjs.updateLocale("vi", { weekStart: 1 });
dayjs.updateLocale("en", { weekStart: 1 });

const RANGE_SEPARATOR = "→";

/** Đếm số ngày lịch (inclusive) trong khoảng */
const countInclusiveDays = (start, end) => {
  const s = dayjs(start).startOf("day");
  const e = dayjs(end).startOf("day");
  if (!s.isValid() || !e.isValid()) return 0;
  return Math.max(0, e.diff(s, "day") + 1);
};

/** Mỗi ngày trong khoảng [start, end] (inclusive) */
const eachDayInRange = (start, end) => {
  const s = dayjs(start).startOf("day");
  const e = dayjs(end).startOf("day");
  if (!s.isValid() || !e.isValid() || e.isBefore(s, "day")) return [];

  const days = [];
  let cur = s;
  while (cur.isBefore(e, "day") || cur.isSame(e, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }
  return days;
};

/**
 * Kiểm tra hạn mức nghỉ phép trên khoảng ngày đã chọn.
 * maxLeaveDuration → chặn (blocking); weekly/monthly → cảnh báo (warning).
 */
const validateLeaveLimits = (start, end, leaveLimits) => {
  const result = { blocking: null, warning: null };
  if (!leaveLimits || !start || !end) return result;

  const {
    maxLeaveDuration,
    isMonthlyLimit,
    monthlyLimitDays,
    isWeeklyLimit,
    weeklyLimitDays,
  } = leaveLimits;

  const totalDays = countInclusiveDays(start, end);
  const maxDuration = Number(maxLeaveDuration);
  if (Number.isFinite(maxDuration) && maxDuration > 0 && totalDays > maxDuration) {
    result.blocking = {
      key: "leave-request.max-leave-duration-exceeded",
      values: { days: maxDuration },
    };
    return result;
  }

  const days = eachDayInRange(start, end);

  if (isWeeklyLimit) {
    const weekLimit = Number(weeklyLimitDays);
    if (Number.isFinite(weekLimit) && weekLimit > 0) {
      const weekCounts = {};
      days.forEach((d) => {
        const key = `${d.isoWeekYear()}-${d.isoWeek()}`;
        weekCounts[key] = (weekCounts[key] || 0) + 1;
      });
      const maxInWeek = Math.max(0, ...Object.values(weekCounts));
      if (maxInWeek > weekLimit) {
        result.warning = {
          key: "leave-request.weekly-leave-limit-warning",
          values: { days: weekLimit },
        };
        return result;
      }
    }
  }

  if (isMonthlyLimit) {
    const monthLimit = Number(monthlyLimitDays);
    if (Number.isFinite(monthLimit) && monthLimit > 0) {
      const monthCounts = {};
      days.forEach((d) => {
        const key = d.format("YYYY-MM");
        monthCounts[key] = (monthCounts[key] || 0) + 1;
      });
      const maxInMonth = Math.max(0, ...Object.values(monthCounts));
      if (maxInMonth > monthLimit) {
        result.warning = {
          key: "leave-request.monthly-leave-limit-warning",
          values: { days: monthLimit },
        };
      }
    }
  }

  return result;
};

const WEEK_DAY_NAME_TO_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const CustomPickersDay = ({
  day,
  selectedStart,
  selectedEnd,
  selectedDates,
  mode,
  ...props
}) => {
  const isRangeLike = mode === "range" || mode === "week";

  const isStart =
    selectedStart &&
    day.isSame(selectedStart, "day");

  const isEnd =
    selectedEnd &&
    day.isSame(selectedEnd, "day");

  const isSameDayRange = isStart && isEnd;

  const isInRange =
    isRangeLike &&
    selectedStart &&
    selectedEnd &&
    !isSameDayRange &&
    day.isAfter(selectedStart, "day") &&
    day.isBefore(selectedEnd, "day");

  // Nền range phủ cả ngày start/end (trừ cùng 1 ngày) để dải màu liền nhau
  const showRangeBg =
    isRangeLike &&
    !isSameDayRange &&
    (isInRange || isStart || isEnd);

  // Tuần bắt đầu T2 (1) → kết thúc CN (0)
  const dayOfWeek = day.day();
  const isFirstDayOfWeek = dayOfWeek === 1;
  const isLastDayOfWeek = dayOfWeek === 0;

  const getWrapperBorderRadius = () => {
    if (!showRangeBg) return 0;

    const roundLeft = isStart || isFirstDayOfWeek;
    const roundRight = isEnd || isLastDayOfWeek;

    if (roundLeft && roundRight) return "8px";
    if (roundLeft) return "8px 0 0 8px";
    if (roundRight) return "0 8px 8px 0";
    return 0;
  };

  const isSelectedMultiple =
    mode === "multiple" &&
    Array.isArray(selectedDates) &&
    selectedDates.some((d) => day.isSame(d, "day"));

  const isEndpoint = isStart || isEnd || isSelectedMultiple;

  const PRIMARY = "#0057FF";
  const RANGE_BG = "#F5F8FF";

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Wrapper full width → nền các ngày trong range dính liền không hở
        bgcolor: showRangeBg ? RANGE_BG : "transparent",
        borderRadius: getWrapperBorderRadius(),
        py: "2px",
      }}
    >
      <PickersDay
        {...props}
        day={day}
        selected={isEndpoint}
        disableMargin
        disableRipple
        disableTouchRipple
        sx={{
          width: 36,
          height: 36,
          margin: 0,

          // Start bo trái, end bo phải; cùng ngày thì bo cả 4 góc
          borderRadius:
            isSameDayRange || isSelectedMultiple
              ? "8px"
              : isStart
                ? "8px 0 0 8px"
                : isEnd
                  ? "0 8px 8px 0"
                  : 0,

          backgroundColor: isEndpoint ? PRIMARY : "transparent",

          color: isEndpoint ? "#fff" : "inherit",

          border: isEndpoint
            ? `1px solid ${PRIMARY}`
            : "1px solid transparent",

          "&:hover": {
            backgroundColor: isEndpoint ? PRIMARY : RANGE_BG,
          },

          "&.Mui-selected": {
            backgroundColor: PRIMARY,
            color: "#fff",
          },

          "&.Mui-selected:hover": {
            backgroundColor: PRIMARY,
          },

          "&.Mui-focusVisible": {
            outline: "none",
            backgroundColor: isEndpoint ? PRIMARY : "transparent",
          },

          "&:focus": {
            outline: "none",
          },

          "&:focus-visible": {
            outline: "none",
          },

          "& .MuiTouchRipple-root": {
            display: "none",
          },
        }}
      />
    </Box>
  );
};

const TimePopupSelect = ({
  label,
  value,
  onChange,
  gap = 0.1,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { t } = useTranslation();

  const getBaseDate = () =>
    value && dayjs(value).isValid()
      ? dayjs(value)
      : dayjs().startOf("day");

  const [inputValue, setInputValue] = useState(() =>
    getBaseDate().format("HH:mm:ss")
  );
  const [hours, setHours] = useState(() => getBaseDate().hour());
  const [minutes, setMinutes] = useState(() => getBaseDate().minute());
  const [seconds, setSeconds] = useState(() => getBaseDate().second());

  const hourLabel = t("common.hours") || "Hours";
  const minuteLabel = t("common.minutes") || "Minutes";
  const secondLabel = t("common.seconds") || "Seconds";

  useEffect(() => {
    if (!value || !dayjs(value).isValid()) return;
    const d = dayjs(value);
    setInputValue(d.format("HH:mm:ss"));
    setHours(d.hour());
    setMinutes(d.minute());
    setSeconds(d.second());
  }, [value]);

  const isValidTime = (val) => {
    const match = val.match(/^(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return false;
    const h = Number(match[1]);
    const m = Number(match[2]);
    const s = Number(match[3]);
    return h <= 23 && m <= 59 && s <= 59;
  };

  const emitTime = (h, m, s) => {
    const safeH = Math.min(Math.max(Number(h) || 0, 0), 23);
    const safeM = Math.min(Math.max(Number(m) || 0, 0), 59);
    const safeS = Math.min(Math.max(Number(s) || 0, 0), 59);
    const formatted = `${String(safeH).padStart(2, "0")}:${String(safeM).padStart(2, "0")}:${String(safeS).padStart(2, "0")}`;

    setInputValue(formatted);
    setHours(safeH);
    setMinutes(safeM);
    setSeconds(safeS);

    const updated = getBaseDate().hour(safeH).minute(safeM).second(safeS);
    onChange(updated.toDate());
  };

  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d:]/g, "");
    if (val.length > 8) return;

    setInputValue(val);

    if (!isValidTime(val)) return;

    const [h, m, s] = val.split(":").map(Number);
    emitTime(h, m, s);
  };

  const handleInputBlur = () => {
    if (!isValidTime(inputValue)) {
      emitTime(hours, minutes, seconds);
    }
  };

  const handleHoursChange = (e) => {
    let h = Number(e.target.value);
    if (Number.isNaN(h)) h = 0;
    if (h > 23) h = 23;
    if (h < 0) h = 0;
    emitTime(h, minutes, seconds);
  };

  const handleMinutesChange = (e) => {
    let m = Number(e.target.value);
    if (Number.isNaN(m)) m = 0;
    if (m > 59) m = 59;
    if (m < 0) m = 0;
    emitTime(hours, m, seconds);
  };

  const handleSecondsChange = (e) => {
    let s = Number(e.target.value);
    if (Number.isNaN(s)) s = 0;
    if (s > 59) s = 59;
    if (s < 0) s = 0;
    emitTime(hours, minutes, s);
  };

  const openPopup = (e) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap={gap}
        sx={{ flexShrink: 0 }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            flexShrink: 0,
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>

        <TextField
          size="small"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder="HH:mm:ss"
          inputProps={{
            maxLength: 8,
            inputMode: "numeric",
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ ml: 0, mr: 0, maxHeight: "unset" }}
              >
                <IconButton
                  size="small"
                  onClick={openPopup}
                  edge="end"
                  aria-label={t("common.select-time")}
                  sx={{ p: 0.5 }}
                >
                  <AccessTimeIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: 120,
            "& .MuiInputBase-root": {
              borderRadius: "8px",
              height: "40px",
              backgroundColor: "#fff",
              pl: 0.3,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px",
            },
            "& .MuiInputBase-input": {
              px: 1,
              py: 0.5,
            },
          }}
        />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 999999,
        }}
      >
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={(event) => {
            const target = event.target;

            if (
              target.closest(".MuiMenu-root") ||
              target.closest(".MuiPopover-root")
            ) {
              return;
            }

            setOpen(false);
          }}
        >
          <Paper
            elevation={5}
            sx={{
              mt: 1,
              p: 2,
              borderRadius: "8px",
              width: 320,
              position: "relative",
              zIndex: 1,
              overflow: "visible",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "8px",
              },
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={3.5}>
                <TextField
                  fullWidth
                  size="small"
                  label={hourLabel}
                  type="number"
                  value={String(hours).padStart(2, "0")}
                  onChange={handleHoursChange}
                  inputProps={{ min: 0, max: 23 }}
                />
              </Grid>

              <Grid item>
                <Typography>:</Typography>
              </Grid>

              <Grid item xs={3.5}>
                <TextField
                  fullWidth
                  size="small"
                  label={minuteLabel}
                  type="number"
                  value={String(minutes).padStart(2, "0")}
                  onChange={handleMinutesChange}
                  inputProps={{ min: 0, max: 59 }}
                />
              </Grid>

              <Grid item>
                <Typography>:</Typography>
              </Grid>

              <Grid item xs={3.5}>
                <TextField
                  fullWidth
                  size="small"
                  label={secondLabel}
                  type="number"
                  value={String(seconds).padStart(2, "0")}
                  onChange={handleSecondsChange}
                  inputProps={{ min: 0, max: 59 }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setOpen(false)}
              >
                OK
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const CustomDateTimePicker = ({
  mode = "range",
  value,
  onChange,
  disableTime = false,
  minDateTime,
  maxDateTime,
  maxDate,          // thêm
  minDate,          // thêm nếu cần
  showOnlyDate = false,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  hideInput = false,
  // Hạn mức nghỉ phép từ leaveByUser (maxLeaveDuration / weekly / monthly)
  leaveLimits = null,
  // Weekly: ngày bắt đầu tuần từ cấu hình hệ thống (Monday, Sunday, ...)
  weekStartDay = "Monday",
}) => {
  const { t, i18n } = useTranslation();
  const { dateTimeFormat } = useAuth();
  const [rangeAnchor, setRangeAnchor] = useState(null);

  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const prevOpenRef = useRef(false);
  const setOpen = (next) => {
    if (controlledOpen === undefined) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  const [calendarMonth, setCalendarMonth] =
    useState(dayjs());

  const [tempValue, setTempValue] =
    useState(
      mode === "multiple"
        ? []
        : mode === "single" ||
          mode === "singleTime"
          ? dayjs()
          : [null, null]
    );

  const effectiveMaxDate = maxDate
    ? dayjs(maxDate)
    : null; // mặc định hôm nay

  const effectiveMinDate = minDate
    ? dayjs(minDate)
    : undefined;

  // So sánh theo timestamp thay vì reference Date — tránh sync lại khi parent re-render
  const syncedValueKey = useMemo(() => {
    if (mode === "multiple") {
      if (!Array.isArray(value) || value.length === 0) {
        return "multiple:empty";
      }
      return `multiple:${value
        .map((d) => (dayjs(d).isValid() ? dayjs(d).valueOf() : ""))
        .join(",")}`;
    }

    if (mode === "single" || mode === "singleTime") {
      if (!value || !dayjs(value).isValid()) {
        return "single:empty";
      }
      return `single:${dayjs(value).valueOf()}`;
    }

    const startKey =
      value?.[0] && dayjs(value[0]).isValid()
        ? dayjs(value[0]).valueOf()
        : "";
    const endKey =
      value?.[1] && dayjs(value[1]).isValid()
        ? dayjs(value[1]).valueOf()
        : "";

    return `range:${startKey},${endKey}`;
  }, [value, mode]);

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    // Popup đang mở và không phải lần vừa mở → giữ tempValue user đang chọn
    if (open && !justOpened) return;

    const now = dayjs();

    if (mode === "multiple") {
      if (Array.isArray(value)) {
        setTempValue(
          value
            .filter((d) => dayjs(d).isValid())
            .map((d) => dayjs(d).startOf("day"))
            .sort((a, b) => a.valueOf() - b.valueOf())
        );
      } else {
        setTempValue([]);
      }
      return;
    }

    if (mode === "single" ||
      mode === "singleTime") {
      if (value && dayjs(value).isValid()) {
        setTempValue(dayjs(value));
      } else {
        setTempValue(now);
      }

      return;
    }

    const start =
      value?.[0] &&
        dayjs(value[0]).isValid()
        ? dayjs(value[0])
        : now.startOf("day");

    const end =
      value?.[1] &&
        dayjs(value[1]).isValid()
        ? dayjs(value[1])
        : now.endOf("day");

    setTempValue([start, end]);
  }, [syncedValueKey, mode, open]);

  const displayFormat = showOnlyDate
    ? "DD/MM/YYYY"
    : "DD/MM/YYYY HH:mm:ss";

  const displayValue = useMemo(() => {
    if (mode === "multiple") {
      if (!Array.isArray(value) || value.length === 0) {
        return "";
      }
      if (value.length === 1 && dayjs(value[0]).isValid()) {
        return dayjs(value[0]).format(displayFormat);
      }
      return t("common.selected-days-count", { count: value.length });
    }

    if (mode === "single" || mode === "singleTime") {
      if (!value || !dayjs(value).isValid()) {
        return "";
      }

      return dayjs(value).format(displayFormat);
    }

    if (
      !value?.[0] ||
      !value?.[1] ||
      !dayjs(value[0]).isValid() ||
      !dayjs(value[1]).isValid()
    ) {
      return "";
    }

    return `${dayjs(value[0]).format(displayFormat)} ${RANGE_SEPARATOR} ${dayjs(value[1]).format(displayFormat)}`;
  }, [value, mode, displayFormat, t]);

  const adapterLocale = i18n.language?.startsWith("vi")
    ? "vi"
    : "en";

  // Đồng bộ locale dayjs để tuần bắt đầu từ thứ 2
  useEffect(() => {
    dayjs.locale(adapterLocale);
  }, [adapterLocale]);

  const dayOfWeekFormatter = (date) => {
    const isVI = adapterLocale === "vi";
    const dayIndex = dayjs(date).day();

    const labels = isVI
      ? ["CN", "T2", "T3", "T4", "T5", "T6", "T7"]
      : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    return labels[dayIndex] ?? "";
  };

  // Khi đổi ngày trên calendar, giữ lại giờ đã chọn từ ca hiện tại nếu có.
  const withDefaultTime = (date, isEnd = false, referenceDate = null) => {
    if (disableTime) return date;

    if (referenceDate && dayjs(referenceDate).isValid()) {
      const old = dayjs(referenceDate);

      return date
        .hour(old.hour())
        .minute(old.minute())
        .second(old.second())
        .millisecond(0);
    }

    return isEnd
      ? date.hour(23).minute(59).second(59)
      : date.hour(0).minute(0).second(0);
  };

  const handleSelectDay = (
    selectedDate
  ) => {
    if (!selectedDate) return;

    if (mode === "multiple") {
      const current = Array.isArray(tempValue) ? [...tempValue] : [];
      const index = current.findIndex((d) =>
        d.isSame(selectedDate, "day")
      );

      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(selectedDate.startOf("day"));
      }

      current.sort((a, b) => a.valueOf() - b.valueOf());
      setTempValue(current);
      return;
    }

    if (
      mode === "single" ||
      mode === "singleTime"
    ) {
      const old = dayjs(tempValue).isValid()
        ? dayjs(tempValue)
        : dayjs();

      const merged = dayjs(selectedDate)
        .startOf("day")
        .hour(old.hour())
        .minute(old.minute())
        .second(old.second())
        .millisecond(0);

      setTempValue(merged);
      setCalendarMonth(merged.startOf("month"));
      return;
    }

    // Weekly: chọn 1 ngày → cả tuần chứa ngày đó
    if (mode === "week") {
      const { start, end } = resolveWeekRange(selectedDate);
      setTempValue([
        withDefaultTime(start, false, tempValue?.[0]),
        withDefaultTime(end, true, tempValue?.[1]),
      ]);
      setCalendarMonth(start.startOf("month"));
      setRangeAnchor(null);
      return;
    }

    // Monthly: chọn 1 ngày trong tháng → cả tháng
    if (mode === "month") {
      const { start, end } = resolveMonthRange(selectedDate);
      setTempValue([
        withDefaultTime(start, false, tempValue?.[0]),
        withDefaultTime(end, true, tempValue?.[1]),
      ]);
      setCalendarMonth(start.startOf("month"));
      setRangeAnchor(null);
      return;
    }

    // CLICK ĐẦU
    if (!rangeAnchor) {
      setRangeAnchor(selectedDate);

      setTempValue([
        withDefaultTime(selectedDate, false, tempValue?.[0]),
        withDefaultTime(selectedDate, true, tempValue?.[1]),
      ]);

      return;
    }

    // CLICK LẠI CÙNG NGÀY
    if (
      selectedDate.isSame(
        rangeAnchor,
        "day"
      )
    ) {
      setTempValue([
        withDefaultTime(selectedDate, false, tempValue?.[0]),
        withDefaultTime(selectedDate, true, tempValue?.[1]),
      ]);

      return;
    }

    // CLICK NGÀY THỨ 2 => TẠO RANGE
    if (
      selectedDate.isBefore(
        rangeAnchor,
        "day"
      )
    ) {
      setTempValue([
        withDefaultTime(selectedDate, false, tempValue?.[0]),
        withDefaultTime(rangeAnchor, true, tempValue?.[1]),
      ]);
    } else {
      setTempValue([
        withDefaultTime(rangeAnchor, false, tempValue?.[0]),
        withDefaultTime(selectedDate, true, tempValue?.[1]),
      ]);
    }

    setRangeAnchor(null);
  };

  const clampDateToBounds = (date) => {
    let result = dayjs(date);
    if (!result.isValid()) return result;

    if (effectiveMinDate && result.isBefore(effectiveMinDate, "day")) {
      result = effectiveMinDate;
    }
    if (effectiveMaxDate && result.isAfter(effectiveMaxDate, "day")) {
      result = effectiveMaxDate;
    }
    return result;
  };

  const resolveWeekRange = (date) => {
    const weekStartsOn = WEEK_DAY_NAME_TO_INDEX[weekStartDay] ?? 1;
    const picked = dayjs(date).startOf("day");
    const diff = (picked.day() - weekStartsOn + 7) % 7;
    let start = picked.subtract(diff, "day").startOf("day");
    let end = start.add(6, "day").endOf("day");
    start = clampDateToBounds(start);
    end = clampDateToBounds(end);
    if (start.isAfter(end, "day")) start = end;
    return { start, end };
  };

  const resolveMonthRange = (date) => {
    let start = dayjs(date).startOf("month");
    let end = dayjs(date).endOf("month");
    start = clampDateToBounds(start);
    end = clampDateToBounds(end);
    if (start.isAfter(end, "day")) start = end;
    return { start, end };
  };

  const handleToday = () => {
    const now = clampDateToBounds(dayjs());

    setCalendarMonth(now);
    setTempValue([
      withDefaultTime(now, false, tempValue?.[0]),
      withDefaultTime(now, true, tempValue?.[1]),
    ]);
  };

  const handleThisWeek = () => {
    const now = dayjs();
    const { start, end } = resolveWeekRange(now);

    setCalendarMonth(now);
    setTempValue([
      withDefaultTime(start, false, tempValue?.[0]),
      withDefaultTime(end, true, tempValue?.[1]),
    ]);
  };

  const handleThisMonth = () => {
    const now = dayjs();
    const { start, end } = resolveMonthRange(now);

    setCalendarMonth(now);
    setTempValue([
      withDefaultTime(start, false, tempValue?.[0]),
      withDefaultTime(end, true, tempValue?.[1]),
    ]);
  };

  const handleReset = () => {
    setRangeAnchor(null);

    if (mode === "multiple") {
      setTempValue([]);
      return;
    }

    if (
      mode === "single" ||
      mode === "singleTime"
    ) {
      setTempValue(dayjs());
    } else {
      setTempValue([
        null,
        null,
      ]);
    }
  };

  const isValidShiftRange = () => {
    if (
      disableTime ||
      !minDateTime ||
      !maxDateTime
    ) {
      return true;
    }

    const min = dayjs(minDateTime);
    const max = dayjs(maxDateTime);

    // Chỉ so sánh theo giờ trong ngày với khung ca (hỗ trợ ca qua đêm, vd 22:00–06:00)
    const isTimeInRange = (date) => {
      if (!date) return false;

      const current = dayjs(date);

      const currentSecond =
        current.hour() * 3600 +
        current.minute() * 60 +
        current.second();

      const minSecond =
        min.hour() * 3600 +
        min.minute() * 60 +
        min.second();

      let maxSecond =
        max.hour() * 3600 +
        max.minute() * 60 +
        max.second();

      // Ca kết thúc 00:00:00 thường nghĩa là hết ngày → coi như 23:59:59
      if (maxSecond === 0 && minSecond > 0) {
        maxSecond = 24 * 3600 - 1;
      }

      // Ca thường: 08:00–18:00
      if (minSecond <= maxSecond) {
        return currentSecond >= minSecond && currentSecond <= maxSecond;
      }

      // Ca qua đêm: 22:00–06:00 → hợp lệ nếu >= start hoặc <= end (vd 00:00–06:00)
      return currentSecond >= minSecond || currentSecond <= maxSecond;
    };

    if (mode === "multiple") {
      return true;
    }

    if (mode === "single" || mode === "singleTime") {
      return isTimeInRange(tempValue);
    }

    // Range: cả start và end phải nằm trong khung giờ ca
    return (
      isTimeInRange(tempValue?.[0]) &&
      isTimeInRange(tempValue?.[1])
    );
  };

  const handleConfirm = () => {
    if (
      (mode === "range" || mode === "week" || mode === "month") &&
      (!tempValue?.[0] ||
        !tempValue?.[1])
    ) {
      toast.error(
        t("common.select-date-range")
      );
      return;
    }

    if (!isValidShiftRange()) {
      toast.error(
        t("common.time-must-in-shift")
      );
      return;
    }

    if (mode === "multiple") {
      if (!Array.isArray(tempValue) || tempValue.length === 0) {
        toast.error(t("common.select-at-least-one-day"));
        return;
      }

      onChange?.(tempValue.map((d) => d.toDate()));
      setRangeAnchor(null);
      setOpen(false);
      return;
    }

    // Validate hạn mức nghỉ phép (ngày/lần chặn; tuần/tháng chỉ cảnh báo)
    if (mode === "range" && leaveLimits) {
      const limitResult = validateLeaveLimits(
        tempValue[0],
        tempValue[1],
        leaveLimits
      );
      if (limitResult.blocking) {
        toast.error(t(limitResult.blocking.key, limitResult.blocking.values));
        return;
      }
      if (limitResult.warning) {
        toast.warn(t(limitResult.warning.key, limitResult.warning.values));
      }
    }

    if (
      mode === "single" ||
      mode === "singleTime"
    ) {
      onChange?.(
        clampDateToBounds(dayjs(tempValue)).toDate()
      );
    } else if (mode === "week") {
      const { start, end } = resolveWeekRange(tempValue?.[0] || tempValue?.[1]);
      onChange?.([start.toDate(), end.toDate()]);
    } else if (mode === "month") {
      const { start, end } = resolveMonthRange(tempValue?.[0] || tempValue?.[1]);
      onChange?.([start.toDate(), end.toDate()]);
    } else {
      const start = clampDateToBounds(dayjs(tempValue[0]));
      let end = clampDateToBounds(dayjs(tempValue[1]));
      if (start.isAfter(end, "day")) {
        end = start;
      }
      onChange?.([
        start.toDate(),
        end.toDate(),
      ]);
    }

    setRangeAnchor(null);
    setOpen(false);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={adapterLocale}
    >
      {!hideInput && (
        <Tooltip
          title={displayValue || ""}
          disableHoverListener={!displayValue}
          arrow
          placement="top"
        >
          <TextField
          fullWidth
          disabled={disabled}
          size="small"
          sx={{
            "& .MuiInputBase-root": {
              height: "40px",
              backgroundColor: "#fff",
              borderRadius: "10px",

              "&.Mui-disabled": {
                backgroundColor: "#e5e7eb",
              }
            },

            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              borderRadius: "10px",
            },

            "& .MuiInputBase-input": {
              height: "40px",
              boxSizing: "border-box",
              padding: "0 14px",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",

              "&.Mui-disabled": {
                WebkitTextFillColor: "#1f2937",
              }
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB !important",
              borderRadius: "10px",
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB !important",
            },

          }}
          value={displayValue}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          placeholder={
            showOnlyDate
              ? mode === "range" || mode === "week" || mode === "month"
                ? `DD/MM/YYYY ${RANGE_SEPARATOR} DD/MM/YYYY`
                : mode === "multiple"
                  ? t("leave-request.select-leave-days")
                  : "DD/MM/YYYY"
              : mode === "single"
                ? dateTimeFormat
                : mode === "multiple"
                  ? t("leave-request.select-leave-days")
                  : `${dateTimeFormat} ${RANGE_SEPARATOR} ${dateTimeFormat}`
          }
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  disabled={disabled}
                  onClick={() =>
                    setOpen(true)
                  }
                >
                  <CalendarV2Icon16pxSvg />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        </Tooltip>
      )}

      {/* Modal xếp chồng trên Dialog — Popper portal bị Dialog aria-hidden/inert nên không click được ngày */}
      <Modal
        open={!disabled && open}
        onClose={() => setOpen(false)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
          <Paper
            elevation={5}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform:
                "translate(-50%, -50%)",
              width: isMobile
                ? "95vw"
                : 400,
              borderRadius: "8px",
              p: 2,
              outline: "none",
            }}
          >
            <DateCalendar
              value={
                mode === "range" || mode === "week" || mode === "month"
                  ? tempValue?.[0]
                  : mode === "multiple"
                    ? null
                    : tempValue
              }
              referenceDate={calendarMonth}
              onMonthChange={(m) =>
                setCalendarMonth(m)
              }
              onChange={handleSelectDay}
              {...(mode === "month"
                ? { views: ["year", "month"], openTo: "month" }
                : {})}
              dayOfWeekFormatter={
                dayOfWeekFormatter
              }
              minDate={effectiveMinDate}
              maxDate={effectiveMaxDate}
              sx={{
                width: "100%",
                maxWidth: "100%",

                "& .MuiDayCalendar-header": {
                  justifyContent: "space-between",
                  px: 0.5,
                },

                "& .MuiDayCalendar-weekDayLabel": {
                  width: "100%",
                  margin: 0,
                },

                // Mỗi ô flex:1 → nền range liền nhau, không còn khoảng trống
                "& .MuiDayCalendar-weekContainer": {
                  justifyContent: "space-between",
                  margin: 0,
                },

                "& .MuiPickersCalendarHeader-root": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },

                "& .MuiPickersDay-root": {
                  width: 36,
                  height: 36,
                  fontSize: "0.95rem",
                  borderRadius: "8px",
                },

                "& .MuiMonthCalendar-root, & .MuiYearCalendar-root": {
                  width: "100%",
                },

                "& .MuiYearCalendar-root": {
                  width: 400
                },

                "& .MuiMonthCalendar-button, & .MuiYearCalendar-button": {
                  height: 44,
                  fontSize: "1rem",
                  borderRadius: "8px",
                },
              }}
              slots={{
                leftArrowIcon:
                  ChevronLeftIcon,
                rightArrowIcon:
                  ChevronRightIcon,
                day: (props) => {
                  // single/singleTime: dùng PickersDay mặc định để chọn ngày hoạt động đúng
                  if (mode === "single" || mode === "singleTime" || mode === "month") {
                    return <PickersDay {...props} />;
                  }

                  return (
                    <CustomPickersDay
                      {...props}
                      mode={mode}
                      selectedStart={
                        mode === "range" || mode === "week"
                          ? tempValue?.[0]
                          : tempValue
                      }
                      selectedEnd={
                        mode === "range" || mode === "week"
                          ? tempValue?.[1]
                          : tempValue
                      }
                      selectedDates={
                        mode === "multiple" ? tempValue : undefined
                      }
                    />
                  );
                },
              }}
            />

            {mode === "range" && (
              <Box
                display="flex"
                justifyContent="space-between"
                mt={1}
                mb={2}
              >
                <Button
                  onClick={handleToday}
                >
                  {t('common.today')}
                </Button>

                <Button
                  onClick={
                    handleThisWeek
                  }
                >
                  {t('common.thisweek')}
                </Button>

                <Button
                  onClick={
                    handleThisMonth
                  }
                >
                  {t('common.thismonth')}
                </Button>
              </Box>
            )}

            {mode === "month" && (
              <Box
                display="flex"
                justifyContent="flex-end"
                mt={1}
                mb={2}
              >
                <Button onClick={handleThisMonth}>
                  {t('common.thismonth')}
                </Button>
              </Box>
            )}

            {mode === "week" && (
              <Box
                display="flex"
                justifyContent="flex-end"
                mt={1}
                mb={2}
              >
                <Button onClick={handleThisWeek}>
                  {t('common.thisweek')}
                </Button>
              </Box>
            )}

            {mode === "multiple" && Array.isArray(tempValue) && tempValue.length > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t("common.selected-days-count", { count: tempValue.length })}
              </Typography>
            )}

            {!disableTime && mode !== "multiple" && (
              <>
                {mode === "single" ||
                  mode === "singleTime" ? (
                  <TimePopupSelect
                    label={t('common.select-time')}
                    value={tempValue}
                    onChange={(newDate) => {
                      setTempValue(dayjs(newDate));
                    }}
                    gap={1}
                  />
                ) : (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <TimePopupSelect
                      label={t('common.start')}
                      value={tempValue?.[0]}
                      gap={0.5}
                      onChange={(newDate) => {
                        setTempValue([
                          dayjs(newDate),
                          tempValue?.[1],
                        ]);
                      }}
                    />

                    <TimePopupSelect
                      label={t('common.end')}
                      value={tempValue?.[1]}
                      gap={0.5}
                      onChange={(newDate) => {
                        setTempValue([
                          tempValue?.[0],
                          dayjs(newDate),
                        ]);
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1}
            >
              <Button
                onClick={
                  handleReset
                }
              >
                {t('button.reset')}
              </Button>

              <ControlpanelPrimaryButton
                variant="contained"
                onClick={
                  handleConfirm
                }
              >
                {t('button.confirm')}
              </ControlpanelPrimaryButton>
            </Box>
          </Paper>
      </Modal>
    </LocalizationProvider>
  );
};

export { TimePopupSelect };
export default CustomDateTimePicker;